import React, { useMemo } from 'react';
import {
    applyTableFilters,
    applyTableSort
} from '../byor/utils/tableFilterUtils';
import { getLabelStyle } from './widgetLabelHelpers';

// Helper function to calculate pagination slice indices
const getPaginationSlice = (pagination) => {
  if (!pagination) return null;
  const startIndex = pagination.startIndex !== undefined 
    ? pagination.startIndex 
    : pagination.currentPage * pagination.itemsPerPage;
  const endIndex = startIndex + pagination.itemsPerPage;
  return { startIndex, endIndex };
};

export const getDisplayHeader = (config, headerName) => {
  return config?.customColumnHeaders?.[headerName] || headerName;
};

const applyHiddenColumns = (config, columnMap) => {
  const hiddenColumns = config?.hiddenColumns || [];
  if (hiddenColumns.length === 0) return {};
  const overrides = {};
  for (const [colName, showKey] of Object.entries(columnMap)) {
    if (hiddenColumns.includes(colName)) {
      overrides[showKey] = false;
    }
  }
  return overrides;
};

// Centralized Privileged Groups Definition
const PRIVILEGED_GROUPS_BASELINE = [
  'Domain Admins',
  'Enterprise Admins',
  'Schema Admins',
  'Administrators',
  'Account Operators',
  'Backup Operators',
  'Server Operators',
  'Print Operators',
  'Network Configuration Operators'
];

/**
 * Data extractor for AD Privileged Admins
 * Identifies users and groups with elevated rights.
 */
export function extractADAdminsData(data, config = {}) {
  const adminsData = [];
  if (!data) return adminsData;

  const scanData = data.data || data.scanData || data || {};
  const adData = scanData['Active Directory Scan'] || scanData['Ad Scan'] || {};
  

  // Extract Users
  let users = adData['AD Users Audit'] || adData['AD Users'] || [];
  if (Array.isArray(users) && users.length > 0 && Array.isArray(users[0])) {
    users = users[0];
  }


  // Extract Groups
  let groups = adData['AD Groups Audit'] || adData['groups'] || [];
  if (Array.isArray(groups) && groups.length > 0 && Array.isArray(groups[0])) {
    groups = groups[0];
  }

  const showOnlyEnabled = config.showOnlyEnabled === true;
  const showOnlyDisabled = config.showOnlyDisabled === true;
  const showOnlyUsers = config.showOnlyUsers === true;
  const showOnlyGroups = config.showOnlyGroups === true;
  const selectedPrivGroups = config.selectedPrivGroups || []; // Array of group names

  // 1. Process Groups First (since users depend on them)
  const privilegedGroupMap = new Map(); // Name -> Group Object
  
  groups.forEach(group => {
    const name = group.name || group.Name || group.GroupName || '';
    const isPrivileged = PRIVILEGED_GROUPS_BASELINE.some(pg => name.toLowerCase().includes(pg.toLowerCase()));
    
    if (isPrivileged) {
      privilegedGroupMap.set(name, group);
      
      if (!showOnlyUsers) {
        // Map to table row
        adminsData.push({
          'Username': name,
          'Type': 'Group',
          'Privileged Groups': name,
          'Status': 'Enabled',
          'Last Logon': '-',
          'Severity': 'warning',
          isGroup: true,
          isEnabled: true
        });
      }
    }
  });

  // 2. Process Users
  users.forEach(user => {
    if (showOnlyGroups) return;

    const username = user.username || user.UserName || user.SamAccountName || '';
    
    // Support 'groups', 'Groups', or 'member_of'
    let userGroups = user.groups || user.Groups || user.member_of || [];
    
    // If it's member_of, it might be an array of DNs. Extract CNs if so.
    if (Array.isArray(userGroups)) {
      userGroups = userGroups.map(g => {
        if (typeof g === 'string' && g.includes('CN=')) {
          const match = g.match(/CN=([^,]+)/);
          return match ? match[1] : g;
        }
        return g;
      });
    }

    const matchedGroups = userGroups.filter(g => 
        typeof g === 'string' && PRIVILEGED_GROUPS_BASELINE.some(pg => g.toLowerCase().includes(pg.toLowerCase()))
    );

    // Also consider is_admin flag if available and no groups matched
    const isExplicitAdmin = user.is_admin === true;
    
    if (matchedGroups.length > 0 || isExplicitAdmin) {
      if (matchedGroups.length === 0 && isExplicitAdmin) {
          matchedGroups.push('Administrator (Flagged)');
      }
      const isEnabled = user.is_disabled !== true && user.is_disabled !== 1;
      const lastLogon = user.last_logon || user.LastLogon || '-';
      
      // Check for Stale Account (> 90 days)
      let isStale = false;
      if (lastLogon && lastLogon !== '-') {
        try {
          let logonDate;
          if (typeof lastLogon === 'string' && lastLogon.includes('.0Z')) {
            const year = parseInt(lastLogon.substring(0, 4));
            const month = parseInt(lastLogon.substring(4, 6)) - 1;
            const day = parseInt(lastLogon.substring(6, 8));
            logonDate = new Date(Date.UTC(year, month, day));
          } else {
            logonDate = new Date(lastLogon);
          }

          if (!isNaN(logonDate.getTime())) {
            const now = new Date();
            const diffTime = Math.abs(now - logonDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays > 90) {
              isStale = true;
            }
          }
        } catch (e) {
          console.warn('Error parsing logon date:', lastLogon);
        }
      }

      // Determine severity
      let severity = 'info';
      if (!isEnabled) {
        severity = 'info'; // Disabled Admin Account -> Informational
      } else if (isStale) {
        severity = 'warning'; // Enabled but Stale -> Warning
      } else {
        const isDomainAdmin = matchedGroups.some(g => g.toLowerCase().includes('domain admins'));
        if (isDomainAdmin) {
          severity = 'critical'; // Enabled Domain Admin -> Critical
        } else {
          severity = 'warning'; // Other Enabled Privileged Admin -> Warning
        }
      }

      adminsData.push({
        'Username': username,
        'Type': 'User',
        'Privileged Groups': matchedGroups.join(', '),
        'Status': isEnabled ? (isStale ? 'Enabled (Stale)' : 'Enabled') : 'Disabled',
        'Last Logon': lastLogon,
        'Severity': severity,
        isGroup: false,
        isEnabled: isEnabled,
        isStale: isStale
      });
    }
  });

  // 3. Apply Filters
  let filteredData = adminsData;

  if (showOnlyEnabled) filteredData = filteredData.filter(d => d.isEnabled);
  if (showOnlyDisabled) filteredData = filteredData.filter(d => !d.isEnabled);
  if (showOnlyUsers) filteredData = filteredData.filter(d => !d.isGroup);
  if (showOnlyGroups) filteredData = filteredData.filter(d => d.isGroup);
  
  if (selectedPrivGroups.length > 0) {
    filteredData = filteredData.filter(d => 
      selectedPrivGroups.some(pg => d['Privileged Groups'].toLowerCase().includes(pg.toLowerCase()))
    );
  }

  return filteredData;
}

export const ADAdminsTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  const baseOverrides = { 
    'No.': 'showNo', 
    'ACCOUNT NAME': 'showName', 
    'ACCOUNT TYPE': 'showType', 
    'PRIVILEGED GROUP': 'showPrivGroups', 
    'ENABLED STATUS': 'showEnabled', 
    'LAST LOGON': 'showLastLogon' 
  };
  const hiddenOverrides = applyHiddenColumns(config, baseOverrides);
  
  const defaultShow = { 
    showNo: true, 
    showName: true, 
    showType: true, 
    showPrivGroups: true, 
    showEnabled: true, 
    showLastLogon: true 
  };

  const {
    title = 'Active Directory Privileged Admins',
    ...restConfig
  } = { ...config, ...defaultShow, ...hiddenOverrides };

  const widgetType = 'ad-admins-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const serializedConfig = JSON.stringify(restConfig);

  const allAdminsData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractADAdminsData(data, restConfig), restConfig.tableFilters),
      restConfig.tableSort
    );
  }, [data, serializedConfig]);

  const displayData = useMemo(() => {
    if (!pagination) return allAdminsData;
    const slice = getPaginationSlice(pagination);

    return allAdminsData.slice(slice.startIndex, slice.endIndex);
  }, [allAdminsData, pagination]);

  const columns = [
    restConfig.showNo ? '0.5fr' : '',
    restConfig.showName ? '2fr' : '',
    restConfig.showType ? '1fr' : '',
    restConfig.showPrivGroups ? '2fr' : '',
    restConfig.showEnabled ? '1fr' : '',
    restConfig.showLastLogon ? '1.5fr' : ''
  ].filter(Boolean);
  const templateColumns = columns.join(' ') || '1fr';

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const renderStatus = (severity) => {
    if (restConfig.showStatusIcon === false) return null;
    let icon = '•';
    let color = '#9ca3af';
    if (severity === 'critical') { icon = '🔴'; color = '#ef4444'; }
    else if (severity === 'warning') { icon = '🟡'; color = '#f59e0b'; }
    else if (severity === 'info') { icon = '🔵'; color = '#3b82f6'; }
    else if (severity === 'success') { icon = '🟢'; color = '#10b981'; }
    return <span style={{ color, marginRight: '6px' }}>{icon}</span>;
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title ? <h3 className="findings-widget-title" style={titleStyle}>{title}</h3> : null}

      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {restConfig.showNo ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'No.')}</div> : null}
            {restConfig.showName ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'ACCOUNT NAME')}</div> : null}
            {restConfig.showType ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'ACCOUNT TYPE')}</div> : null}
            {restConfig.showPrivGroups ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'PRIVILEGED GROUP')}</div> : null}
            {restConfig.showEnabled ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'ENABLED STATUS')}</div> : null}
            {restConfig.showLastLogon ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'LAST LOGIN')}</div> : null}
          </div>

          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <span style={{ fontStyle: 'italic', margin: 0 }}>No privileged Active Directory accounts detected.</span>
              </div>
            ) : (
               <React.Fragment>
                 {displayData.map((item, index) => {
                   const startIndex = pagination && pagination.startIndex !== undefined
                     ? pagination.startIndex
                     : (pagination ? pagination.currentPage * pagination.itemsPerPage : 0);
                   const actualNumber = startIndex + index + 1;
                   const uniqueKey = `ad-admin-${item['Username']}-${item['Type']}-${index}`;
                   
                   return (
                     <div key={uniqueKey} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleMerged }}>
                       {restConfig.showNo ? <div className="port-ip" style={rowStyleMerged}><span>{actualNumber}</span></div> : null}
                       {restConfig.showName ? (
                         <div className="port-hostname" style={{ ...rowStyleMerged, fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                           {renderStatus(item['Severity'])}
                           <span>{item['Username']}</span>
                         </div>
                       ) : null}
                       {restConfig.showType ? <div className="port-service" style={rowStyleMerged}><span>{item['Type']}</span></div> : null}
                       {restConfig.showPrivGroups ? <div className="port-service" style={rowStyleMerged}><span>{item['Privileged Groups']}</span></div> : null}
                       {restConfig.showEnabled ? (
                         <div className="port-service" style={{ ...rowStyleMerged, color: item.isEnabled ? 'inherit' : '#9ca3af' }}>
                           <span>{item['Status']}</span>
                         </div>
                       ) : null}
                       {restConfig.showLastLogon ? <div className="port-service" style={rowStyleMerged}><span>{item['Last Logon']}</span></div> : null}
                     </div>
                   );
                 })}
               </React.Fragment>
            )}
          </div>
      </div>
    </div>
  );
};
